import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Input, Form, Modal, message, Space, Popconfirm, Select, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getAllCategories, createCategory, updateCategory, deleteCategory, getVendors, clearCategoriesCache } from '../../api/accounting';

const CategoryTab = ({ selectedBrand = null }) => {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render counter
  
  // Track current fetch to prevent race conditions
  const fetchIdRef = React.useRef(0);

  const fetchCategories = useCallback(async (filters = {}, useCurrentFilters = false) => {
    // Increment fetch ID to track this request
    const currentFetchId = ++fetchIdRef.current;
    
    setLoading(true);
    try {
      // CRITICAL: Only fetch categories for the selected brand
      if (!selectedBrand) {
        setCategories([]);
        setLoading(false);
        return;
      }
      
      // Always filter by selected brand
      filters.brand = selectedBrand.id;
      
      // If useCurrentFilters is true, apply current search state
      if (useCurrentFilters && searchText) {
        filters.name = searchText;
      }
      
      const response = await getAllCategories(filters);
      
      // Only update state if this is still the latest request
      if (currentFetchId !== fetchIdRef.current) {
        return; // A newer request has been made, ignore this result
      }
      
      setCategories(Array.isArray(response.data) ? [...response.data] : []);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error('CategoryTab: Error fetching categories:', error);
      message.error('Failed to fetch categories: ' + (error.response?.data?.detail || error.message));
      setCategories([]);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedBrand, searchText]);

  const fetchVendors = useCallback(async () => {
    try {
      // CRITICAL: Only fetch vendors for the selected brand
      if (!selectedBrand) {
        console.log("CategoryTab: No brand selected, cannot fetch vendors");
        setVendors([]);
        return;
      }
      
      const filters = { brand: selectedBrand.id };
      const response = await getVendors(filters);
      
      // Handle both direct array response and wrapped response
      let vendorsData = response.data;
      if (Array.isArray(vendorsData)) {
        setVendors(vendorsData);
      } else if (vendorsData && Array.isArray(vendorsData.results)) {
        setVendors(vendorsData.results);
      } else {
        setVendors([]);
      }
    } catch (error) {
      console.error('CategoryTab: Error fetching vendors:', error);
      console.error('CategoryTab: Error response:', error.response);
      console.error('CategoryTab: Error details:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "Unknown error";
      message.error('Failed to fetch vendors: ' + errorMsg);
      setVendors([]);
    }
  }, [selectedBrand]);

  // Fetch categories and vendors on component mount or when selectedBrand ID changes
  // Using selectedBrand?.id to prevent unnecessary refetches when object reference changes
  const selectedBrandId = selectedBrand?.id;
  useEffect(() => {
    if (selectedBrand) {
      fetchCategories();
      fetchVendors();
    } else {
      setCategories([]);
      setVendors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId]);

  const handleSearch = () => {
    const filters = {};
    if (searchText) filters.name = searchText;
    fetchCategories(filters);
  };

  const resetSearch = () => {
    setSearchText('');
    fetchCategories();
  };

  const showAddModal = () => {
    setEditingCategory(null);
    form.resetFields();
    // Set default values
    form.setFieldsValue({
      category_type: 'product',
      active: true
    });
    // Ensure vendors are fetched when modal opens
    if (selectedBrand && vendors.length === 0) {
      fetchVendors();
    }
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingCategory(record);
    // Fetch vendors first if not already loaded
    if (selectedBrand && vendors.length === 0) {
      fetchVendors();
    }
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      vendor: record.vendor,
      category_type: record.category_type || 'product',
      active: record.active !== undefined ? record.active : true
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Add brand if selected
      if (selectedBrand) {
        values.brand = selectedBrand.id;
      } else {
        message.error('Please select a brand first');
        return;
      }
      
      // Ensure all required fields have values
      if (!values.category_type) values.category_type = 'product';
      if (values.active === undefined) values.active = true;
      
      if (editingCategory) {
        const response = await updateCategory(editingCategory.id, values);
        // Handle both axios wrapped response and direct data response
        const updatedCategory = response?.data?.id ? response.data : (response?.id ? response : { ...editingCategory, ...values });
        message.success('Category updated successfully');
        
        // Immediately update the item in state (optimistic update)
        setCategories(prevCategories => {
          const newCategories = prevCategories.map(c => 
            c.id === editingCategory.id ? { ...c, ...values, ...updatedCategory } : c
          );
          return [...newCategories]; // Force new array reference
        });
      } else {
        // CREATE NEW CATEGORY
        await createCategory(values);
        
        message.success('Category created successfully');
        
        // Clear categories cache so other components fetch fresh data
        clearCategoriesCache();
        
        // Clear search to show all categories
        setSearchText('');
        
        // Refetch categories to get the newly created one with server-generated fields
        await fetchCategories();
        
        // Force table re-render
        setRefreshKey(prev => prev + 1);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('categoriesUpdated'));
        
        setIsModalVisible(false);
        form.resetFields();
        return; // Exit early for create path
      }
      
      // Clear categories cache so other components fetch fresh data
      clearCategoriesCache();
      
      // For UPDATE path
      setIsModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
      setSearchText('');
      window.dispatchEvent(new CustomEvent('categoriesUpdated'));
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      error.response?.data?.error || 
                      error.message || 
                      "Unknown error";
      message.error('Failed to save category: ' + errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory(id);
      message.success('Category deleted successfully');
      
      // Clear categories cache so other components fetch fresh data
      clearCategoriesCache();
      
      // Immediately remove the deleted item from state (optimistic update)
      setCategories(prevCategories => prevCategories.filter(c => c.id !== id));
      
      // Force table re-render
      setRefreshKey(prev => prev + 1);
      
      // Dispatch event to notify other components to refresh categories
      window.dispatchEvent(new CustomEvent('categoriesUpdated'));
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('Failed to delete category');
    }
  };


  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      render: (vendorId) => {
        const vendor = vendors.find(v => v.id === vendorId);
        return vendor ? vendor.name : '-';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record)}
            type="primary"
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              type="primary" 
              danger 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];



  // Fetch vendors when modal opens if they haven't been fetched yet
  useEffect(() => {
    if (isModalVisible && selectedBrand && vendors.length === 0) {
      fetchVendors();
    }
  }, [isModalVisible, selectedBrand, vendors.length, fetchVendors]);
  return (
    <Card title="Categories Management" variant="outlined">
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Search by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            onPressEnter={handleSearch}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
          <Button onClick={resetSearch}>Reset</Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={showAddModal}
            style={{ marginLeft: 'auto' }}
          >
            Add Category
          </Button>
        </Space>
      </div>

      <Table 
        key={refreshKey}
        columns={columns} 
        dataSource={categories} 
        rowKey="id" 
        loading={loading}
        pagination={{ 
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          showTotal: (total) => `Total ${total} categories`
        }}
      />

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="vendor"
            label="Vendor"
            rules={[{ required: true, message: 'Please select a vendor' }]}
          >
            <Select
              placeholder="Select a vendor"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent={vendors.length === 0 ? "No vendors found. Please ensure a brand is selected and vendors exist for this brand." : "No vendors match your search"}
              loading={loading}
            >
              {vendors.map(vendor => (
                <Select.Option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          

          <Form.Item
            name="active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CategoryTab;